import React from 'react';
import { UseUserList } from '../hooks';
import { Link, useParams } from 'react-router-dom';
import DeleteDialog from 'core/components/shared/DeleteDialog';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    InputAdornment,
    IconButton,
    Chip,
    CircularProgress,
    Typography,
    Stack,
    Tooltip,
    Avatar
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTheme } from '@mui/material/styles';
import ListToolbar from 'cms/components/ListToolbar';
import {
    getProfileAvatarUrl,
    getProfileDisplayName,
    getProfileAvatarLetter,
    nameToAvatarColor,
} from 'features/feature_job/utils/profileDisplay';

function UserList() {
    const { data, isLoading, isError, removeItemFromList, refetch } = UseUserList();
    const [deleteDialogIsOpen, setDeleteDialogIsOpen] = React.useState(false);
    const [selectedId, setSelectedId] = React.useState(0);
    const [refresh, setRefresh] = React.useState(0);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [updatingUserId, setUpdatingUserId] = React.useState(null);
    const { id } = useParams();
    const theme = useTheme();

    const getRoleChipProps = (role) => {
        const normalizedRole = String(role || '').toLowerCase();
        if (normalizedRole === 'employer') {
            return {
                variant: 'outlined',
                sx: {
                    color: '#9db9f2',
                    bgcolor: 'rgba(96,140,230,0.14)',
                    borderColor: 'rgba(96,140,230,0.35)',
                    fontWeight: 600,
                },
            };
        }
        if (normalizedRole === 'employerr') {
            return {
                variant: 'outlined',
                sx: {
                    color: '#c3a6f2',
                    bgcolor: 'rgba(168,120,235,0.14)',
                    borderColor: 'rgba(168,120,235,0.35)',
                    fontWeight: 600,
                },
            };
        }
        if (normalizedRole === 'employee') {
            return {
                variant: 'outlined',
                sx: {
                    color: '#7fd6ac',
                    bgcolor: 'rgba(80,190,140,0.14)',
                    borderColor: 'rgba(80,190,140,0.35)',
                    fontWeight: 600,
                },
            };
        }
        return {
            variant: 'outlined',
            sx: {
                color: 'text.secondary',
                borderColor: 'divider',
            },
        };
    };

    const handleToggleApproval = async (userId, currentStatus) => {
        setUpdatingUserId(userId);
        try {
            const { UserHttpService } = await import('core/services/user');
            await UserHttpService.updateUserVerification(userId, !currentStatus);
            refetch();
        } catch (error) {
            console.error('Error updating user approval:', error);
        } finally {
            setUpdatingUserId(null);
        }
    };

    const _handleDelete = (e, id) => {
        setSelectedId(id);
        setRefresh(refresh + 1);
        setDeleteDialogIsOpen(true);
    };

    const _onDeleteDone = (id) => {
        setDeleteDialogIsOpen(false);
        removeItemFromList(selectedId);
    };

    // Filter users based on search term
    const filteredUsers = React.useMemo(() => {
        if (!data?.results) return [];
        if (!searchTerm) return data.results;
        
        const term = searchTerm.toLowerCase();
        return data.results.filter(item => {
            const fullName = `${item.profile?.first_name || ''} ${item.profile?.last_name || ''}`.toLowerCase();
            const email = item.email?.toLowerCase() || '';
            return fullName.includes(term) || email.includes(term) || item.id.toString().includes(term);
        });
    }, [data, searchTerm]);

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return (
            <Paper sx={{ p: 3 }}>
                <Typography color="error">Failed to load users. Please try again.</Typography>
            </Paper>
        );
    }

    return (
        <Stack spacing={2}>
            <ListToolbar
                title="Users"
                subtitle={`${filteredUsers.length} user${filteredUsers.length === 1 ? '' : 's'}`}
                search={{
                    value: searchTerm,
                    onChange: setSearchTerm,
                    placeholder: 'Search users…'
                }}
                onRefresh={() => refetch()}
            />

            <Box>
                {/* Table */}
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>ID</strong></TableCell>
                                <TableCell><strong>Avatar</strong></TableCell>
                                <TableCell><strong>Name</strong></TableCell>
                                <TableCell><strong>Role</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell align="right"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredUsers.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {searchTerm ? 'No users found matching your search.' : 'No users found.'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredUsers.map((item) => {
                                    const createdDate = item.created_at ? new Date(item.created_at) : null;
                                    const lastOnlineDate = item.last_online ? new Date(item.last_online) : null;
                                    const hasCreatedAt = createdDate && !isNaN(createdDate.getTime());
                                    const hasLastOnline = lastOnlineDate && !isNaN(lastOnlineDate.getTime());
                                    const isUpdating = updatingUserId === item.id;
                                    const profile = item.profile;
                                    const displayName = getProfileDisplayName(profile, item);
                                    const avatarSrc = getProfileAvatarUrl(profile);

                                    return (
                                        <TableRow 
                                            key={item.id + "item"}
                                            sx={{
                                                '&:hover': {
                                                    bgcolor: 'action.hover'
                                                }
                                            }}
                                        >
                                            <TableCell>{item.id}</TableCell>
                                            <TableCell>
                                                <Box
                                                    component={Link}
                                                    to={`/cms/user/${item.id}`}
                                                    sx={{ display: 'inline-flex', textDecoration: 'none' }}
                                                >
                                                    <Avatar
                                                        src={avatarSrc || undefined}
                                                        alt={displayName}
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            bgcolor: nameToAvatarColor(displayName),
                                                            fontSize: '1rem',
                                                            fontWeight: 700,
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        {getProfileAvatarLetter(profile, item)}
                                                    </Avatar>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box>
                                                    <Typography
                                                        component={Link}
                                                        to={`/cms/user/${item.id}`}
                                                        variant="body2"
                                                        fontWeight={500}
                                                        sx={{
                                                            display: 'block',
                                                            color: 'primary.main',
                                                            textDecoration: 'none',
                                                            '&:hover': {
                                                                textDecoration: 'underline',
                                                            },
                                                        }}
                                                    >
                                                        {item.profile?.first_name || ''} {item.profile?.last_name || ''}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                                        {item.email || '—'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {(() => {
                                                    const chipProps = getRoleChipProps(item.profile?.role);
                                                    return (
                                                <Chip
                                                    label={item.profile?.role || '—'}
                                                    size="small"
                                                    variant={chipProps.variant}
                                                    sx={chipProps.sx}
                                                />
                                                    );
                                                })()}
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    {item.is_verified ? (
                                                        <Chip 
                                                            label="Approved" 
                                                            color="success" 
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    ) : (
                                                        <Chip 
                                                            label="Pending" 
                                                            color="warning" 
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                    <Tooltip title={item.is_verified ? "Click to unapprove" : "Click to approve"}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleToggleApproval(item.id, item.is_verified)}
                                                            disabled={isUpdating}
                                                            color={item.is_verified ? "success" : "default"}
                                                        >
                                                            {isUpdating ? (
                                                                <CircularProgress size={16} />
                                                            ) : item.is_verified ? (
                                                                <CheckCircleIcon fontSize="small" />
                                                            ) : (
                                                                <CancelIcon fontSize="small" />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                                                    {hasCreatedAt && (
                                                        <Tooltip title={createdDate ? 'Created at: ' + createdDate.toLocaleDateString() : ''}>
                                                            <CalendarTodayIcon 
                                                                fontSize="small" 
                                                                sx={{ 
                                                                    color: 'text.secondary',
                                                                    fontSize: '18px'
                                                                }} 
                                                            />
                                                        </Tooltip>
                                                    )}
                                                    {hasLastOnline && (
                                                        <Tooltip title={lastOnlineDate ? 'Last online: ' + lastOnlineDate.toLocaleDateString() : ''}>
                                                            <AccessTimeIcon 
                                                                fontSize="small" 
                                                                sx={{ 
                                                                    color: 'text.secondary',
                                                                    fontSize: '18px'
                                                                }} 
                                                            />
                                                        </Tooltip>
                                                    )}
                                                    <IconButton
                                                        component={Link}
                                                        to={`/cms/user/${item.id}`}
                                                        size="small"
                                                        color="primary"
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            _handleDelete(e, item.id);
                                                        }}
                                                        size="small"
                                                        color="error"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <DeleteDialog
                url={`/auth/user/delete/${selectedId}/`}
                isOpen={deleteDialogIsOpen}
                key={"mdl" + deleteDialogIsOpen + selectedId}
                setOpen={setDeleteDialogIsOpen}
                notifyDone={_onDeleteDone}
            />
        </Stack>
    );
}

export default UserList;
